const discountFieldElement = 'discount-field';

if (!customElements.get(discountFieldElement)) {
  class DiscountField extends HTMLElement {
    constructor() {
      super();
      this.boundHandleApplyDiscount = this.handleApplyDiscount.bind(this);
      this.boundHandleRemoveDiscount = this.handleRemoveDiscount.bind(this);
      this.boundHandleAddAnother = this.handleAddAnother.bind(this);
      this.boundHandleToggleForm = this.handleToggleForm.bind(this);
      this.boundHandleKeyPress = this.handleKeyPress.bind(this);
      this.boundInit = this.init.bind(this);
      this.sectionId = this.getAttribute('section-id');
    }

    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.cleanupEventListeners();
    }

    init() {
      this.initializeElements();
      this.initializeState();
      this.setupEventListeners();
      this.setInitialFormState();
      this.loadExistingDiscountCodes();
      this.logDebug('Component initialized');
    }

    initializeElements() {
      this.elements = {
        input: this.querySelector('input[name="discount"]'),
        applyButton: this.querySelector('.js-apply-discount'),
        inputWrapper: this.querySelector('.discount-field__input-wrapper'),
        content: this.querySelector('.discount-field__content'),
        toggleForm: this.querySelector('.js-toggle-form'),
        errorDisplay: this.querySelector('.discount-field__error-message'),
        cartForm: document.querySelector('fvb-cart'),
        list: this.querySelector('.discount-field__list')
      };
    }

    initializeState() {
      this.debugMode = this.dataset.debugMode === 'true';
      this.strings = window.promoCodeStrings || {};
      this.discountCodes = this.parseDiscountCodes();
      this.discountDetails = [];
      this.currentOperation = null;
      this.currentDiscountCode = null;
    }

    refresh() {
      const cartContainer = document.querySelector('.cart-container');
      if (!cartContainer || !this.sectionId) {
        return;
      }

      fetch(`/?section_id=${this.sectionId}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.text();
        })
        .then((response) => {
          cartContainer.innerHTML = response;
        });
    }

    parseDiscountCodes() {
      const discountCodesString = this.dataset.discountCodes;

      if (!discountCodesString || discountCodesString === '') {
        return [];
      }

      return discountCodesString
        .split(',')
        .map(code => code.trim())
        .filter(code => code !== '');
    }

    setInitialFormState() {
      if (this.elements.content) {
        this.isFormOpen = !this.elements.content.classList.contains('active');
      } else {
        this.isFormOpen = false;
      }
    }

    setupEventListeners() {
      this.addEventListeners();
    }

    addEventListeners() {
      if (this.elements.applyButton) {
        this.elements.applyButton.addEventListener('click', this.boundHandleApplyDiscount);
      }

      if (this.elements.toggleForm) {
        this.elements.toggleForm.addEventListener('click', this.boundHandleToggleForm);
      }

      if (this.elements.input) {
        this.elements.input.addEventListener('keypress', this.boundHandleKeyPress);
      }

      this.setupRemoveButtonListeners();
    }

    cleanupEventListeners() {
      if (this.elements.applyButton) {
        this.elements.applyButton.removeEventListener('click', this.boundHandleApplyDiscount);
      }

      if (this.elements.toggleForm) {
        this.elements.toggleForm.removeEventListener('click', this.boundHandleToggleForm);
      }

      if (this.elements.input) {
        this.elements.input.removeEventListener('keypress', this.boundHandleKeyPress);
      }
    }

    handleToggleForm() {
      this.isFormOpen = !this.isFormOpen;
      if (this.elements.content) {
        if (this.isFormOpen) {
          this.elements.content.classList.remove('active');
        } else {
          this.elements.content.classList.add('active');
        }
      }
    }

    handleKeyPress(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleApplyDiscount();
      }
    }

    handleApplyDiscount() {
      const discountCode = this.getInputValue();

      this.logDebug('Apply discount code requested', { discountCode });

      if (this.validateDiscountCode(discountCode)) {
        this.applyDiscountCode(discountCode);
      }
    }

    validateDiscountCode(discountCode) {
      const validationResult = this.performValidation(discountCode);

      if (!validationResult.isValid) {
        this.showError(validationResult.errorMessage);
        this.logDebug('Validation failed', { discountCode, reason: validationResult.errorMessage });
        return false;
      }

      this.clearError();
      this.logDebug('Validation passed', { discountCode });

      return true;
    }

    performValidation(discountCode) {
      if (this.isEmpty(discountCode)) {
        return {
          isValid: false,
          errorMessage: this.strings.empty || 'Please enter a discount code'
        };
      }

      if (this.isAlreadyApplied(discountCode)) {
        return {
          isValid: false,
          errorMessage: this.strings.already_applied || 'Discount code already applied'
        };
      }

      return { isValid: true };
    }

    isEmpty(discountCode) {
      return !discountCode || discountCode.trim() === '';
    }

    isAlreadyApplied(discountCode) {
      return this.discountCodes.some(code =>
        code.toLowerCase() === discountCode.toLowerCase()
      );
    }

    applyDiscountCode(discountCode) {
      this.addToDiscountCodes(discountCode);
      this.currentOperation = 'apply';
      this.currentDiscountCode = discountCode;
      this.updateCartDiscounts();
    }

    addToDiscountCodes(discountCode) {
      if (!this.discountCodes.includes(discountCode)) {
        this.discountCodes.push(discountCode);
        this.logDebug('Added to discount codes', { discountCode, allCodes: this.discountCodes });
      }
    }

    async updateCartDiscounts() {
      try {
        this.setLoadingState(true);
        this.logDebug('Updating cart discounts', { discountCodes: this.discountCodes });

        const formData = this.createFormData();
        const response = await this.sendCartUpdateRequest(formData);

        const success = await this.handleCartUpdateResponse(response);

        if (success) {
          this.refresh();
        }
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.setLoadingState(false);
      }
    }

    createFormData() {
      const formData = new FormData();
      const discountString = this.discountCodes.join(',');

      formData.append('discount', discountString);
      return formData;
    }

    async sendCartUpdateRequest(formData) {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
      }

      return response.json();
    }

    async handleCartUpdateResponse(data) {
      this.logDebug('Cart update response received', { data });
      this.updateDiscountCodesFromResponse(data);

      if (this.currentOperation === 'apply') {
        const appliedCode = this.findAppliedCode(data, this.currentDiscountCode);

        if (appliedCode) {
          if (appliedCode.applicable) {
            this.handleSuccessfulApplication();
          } else {
            this.showError(this.strings.not_applicable || 'This discount code is not applicable');

            if (this.currentDiscountCode) {
              this.discountCodes = this.discountCodes.filter(code =>
                code.toLowerCase() !== this.currentDiscountCode.toLowerCase()
              );
            }

            this.currentOperation = null;
            this.currentDiscountCode = null;
            return false;
          }
        } else {
          this.showError(this.strings.failed || 'Failed to apply discount code');
          this.currentOperation = null;
          this.currentDiscountCode = null;
          return false;
        }
      }

      this.currentOperation = null;
      this.currentDiscountCode = null;

      return true;
    }

    findAppliedCode(data, discountCode) {
      const codeToFind = discountCode || this.getInputValue();
      if (!codeToFind) return null;

      if (data.discount_codes && data.discount_codes.length > 0) {
        return data.discount_codes.find(code =>
          code.code.toLowerCase() === codeToFind.toLowerCase()
        );
      }

      if (data.discount_applications && data.discount_applications.length > 0) {
        return data.discount_applications.find(app =>
          app.type === 'discount_code' &&
          (app.title || app.code || '').toLowerCase() === codeToFind.toLowerCase()
        );
      }

      return null;
    }

    handleSuccessfulApplication() {
      this.logDebug('Discount code applied successfully');
      this.clearInput();
      this.clearError();
    }

    updateDiscountCodesFromResponse(data) {
      const discountApplications = (data.discount_applications || []).filter(discountApplication => discountApplication.type === 'discount_code');

      if (data.discount_codes && data.discount_codes.length > 0) {
        this.discountDetails = data.discount_codes
          .filter(discountCode => discountCode.applicable)
          .map((discountCode, index) => {
            const matchingDiscountApplication = discountApplications.find(discountApplication =>
              discountApplication.title && discountApplication.title.toLowerCase() === discountCode.code.toLowerCase()
            ) || discountApplications[index] || null;
            return {
              code: discountCode.code,
              title: matchingDiscountApplication ? matchingDiscountApplication.title : '',
              amount: discountCode.amount || ''
            };
          });
      } else if (discountApplications.length > 0) {
        this.discountDetails = discountApplications.map(discountApplication => ({
          code: discountApplication.title || discountApplication.code || '',
          title: discountApplication.title || '',
          amount: ''
        }));
      } else {
        this.discountDetails = [];
      }

      this.discountCodes = this.discountDetails.map(discountDetail => discountDetail.code);
      this.logDebug('Updated discount codes from response', { discountDetails: this.discountDetails });
    }

    async loadExistingDiscountCodes() {
      try {
        const response = await fetch('/cart.js');
        const data = await response.json();

        this.logDebug('Loaded existing cart data', {
          data,
          discount_codes: data.discount_codes,
          discount_applications: data.discount_applications
        });

        this.updateDiscountCodesFromResponse(data);
        this.updateDiscountCodesDisplay();
      } catch (error) {
        console.error('[DiscountField] Failed to load existing discount codes:', error);
        this.logDebug('Failed to load existing discount codes', { error: error.message });
      }
    }

    handleApiError(error) {
      this.logDebug('API error occurred', { error: error.message });
      console.error('DiscountField API Error:', error);
      this.showError(this.strings.error || this.strings.failed || 'An error occurred while applying the discount code');
    }

    setLoadingState(isLoading) {
      if (this.elements.applyButton) {
        this.elements.applyButton.disabled = isLoading;
        this.elements.applyButton.textContent = isLoading
          ? (this.strings.loading || 'Applying...')
          : (this.strings.button || this.strings.apply || 'Apply');
      }
    }

    showError(message) {
      if (this.elements.input) {
        this.elements.input.classList.add('error');
      }

      if (this.elements.errorDisplay) {
        this.elements.errorDisplay.textContent = message;
        this.elements.errorDisplay.classList.add('show');
      }
    }

    clearError() {
      if (this.elements.input) {
        this.elements.input.classList.remove('error');
      }

      if (this.elements.errorDisplay) {
        this.elements.errorDisplay.textContent = '';
        this.elements.errorDisplay.classList.remove('show');
      }
    }

    clearInput() {
      if (this.elements.input) {
        this.elements.input.value = '';
      }
    }

    getInputValue() {
      return this.elements.input?.value?.trim() || '';
    }

    logDebug(message, data = null) {
      if (!this.debugMode) return;

      const prefix = '[DiscountField]';

      if (data) {
        console.log(`${prefix} ${message}:`, data);
      } else {
        console.log(`${prefix} ${message}`);
      }
    }

    handleRemoveDiscount(event) {
      const discountCode = event.target.closest('.js-remove-promo-code')?.dataset.promoCode ||
        event.target.closest('.js-remove-promo-code')?.dataset.discountCode;

      if (discountCode) {
        this.logDebug('Remove discount code requested', { discountCode });
        this.removeDiscountCode(discountCode);
      }
    }

    removeDiscountCode(discountCode) {
      this.removeFromDiscountCodes(discountCode);
      this.currentOperation = 'remove';
      this.currentDiscountCode = discountCode;
      this.updateCartDiscounts();
    }

    removeFromDiscountCodes(discountCode) {
      this.discountCodes = this.discountCodes.filter(code =>
        code.toLowerCase() !== discountCode.toLowerCase()
      );
      this.logDebug('Removed from discount codes', { discountCode, allCodes: this.discountCodes });
    }

    updateDiscountCodesDisplay() {
      let listElement = this.elements.list;

      if (!listElement) {
        if (!this.elements.content) return;
          listElement = document.createElement('div');
          listElement.className = 'discount-field__list';
          this.elements.content.insertAdjacentElement('afterend', listElement);
          this.elements.list = listElement;
      }

      if (this.discountCodes.length > 0 && this.elements.content) {
        this.elements.content.classList.add('active');
        this.isFormOpen = false;
      }

      listElement.innerHTML = '';

      const couponCodeLabel = this.strings.coupon_code_label || '';
      const removeLabel = this.strings.remove || '';
      const addLabel = this.strings.add || '';

      this.discountDetails.forEach(detail => {
        const discountItem = document.createElement('div');
        discountItem.className = 'cart-discount';

        let descriptionLine = '';
        if (detail.title) {
          const amountString = detail.amount
            ? ` (€ - ${parseFloat(detail.amount).toFixed(2).replace('.', ',')})`
            : '';
          descriptionLine = `<p class="cart-discount__description">${detail.title}${amountString}</p>`;
        }

        discountItem.innerHTML = `
          <p class="cart-discount__label">${couponCodeLabel}: ${detail.code}</p>
          ${descriptionLine}
          <div class="cart-discount__actions">
            <button
              type="button"
              class="cart-discount__remove js-remove-promo-code link"
              data-promo-code="${detail.code}"
              data-discount-code="${detail.code}"
              aria-label="Remove ${detail.code}"
            >${removeLabel}</button>
            <button
              type="button"
              class="js-add-another link"
            >${addLabel}</button>
          </div>
        `;
        listElement.insertAdjacentElement('beforeend', discountItem);
      });

      this.setupRemoveButtonListeners();
    }

    handleAddAnother() {
      if (this.elements.content) {
        this.elements.content.classList.add('active');
        this.isFormOpen = false;
      }
      if (this.elements.input) {
        this.elements.input.focus();
      }
    }

    setupRemoveButtonListeners() {
      const removeButtons = this.querySelectorAll('.js-remove-promo-code');
      removeButtons.forEach(button => {
        button.removeEventListener('click', this.boundHandleRemoveDiscount);
        button.addEventListener('click', this.boundHandleRemoveDiscount);
      });

      const addButtons = this.querySelectorAll('.js-add-another');
      addButtons.forEach(button => {
        button.removeEventListener('click', this.boundHandleAddAnother);
        button.addEventListener('click', this.boundHandleAddAnother);
      });
    }
  }

  customElements.define(discountFieldElement, DiscountField);
}
